import { sortBy } from "es-toolkit";

// import { keyToSymbol } from "@/models/nodes/utils";
import { hasOneCodepoint } from "@/utils/codePointLength";

import { constants } from "../constantSuggestionsProvider";
import { functions } from "../functionSuggestionsProvider";
import { serviceFunctions } from "../serviceFunctionSuggestionsProvider";
import { createSuggestionFromNode } from "../utils";
import { variables } from "../variableSuggestionsProvider";

import type {
  FunctionSuggestionProps,
  MultiSuggestion,
  SingleSuggestion,
  SuggestionItem,
} from "@/components/suggestions/types";
import type { NodeLookupModel } from "@/models/NodeLookupModel";
import type { NodeModel } from "@/models/nodes/types";

export type TextVariantProps = {
  knownSymbolKeywords: string[];
  knownSuggestionMap: Map<string, SingleSuggestion[]>;
  existingNodes: Map<string, (NodeModel<"constant" | "variable" | "function"> | null)[]>;
  nodeLookup: NodeLookupModel | null;
};

const ALLOWED_SINGLE_LETTER_SYMBOLS = ["i", "d", "f", "e"];

export type SuggestionVariant = MultiSuggestion & {
  variantTypes: ("plain" | "power" | "index")[];
  maxRepetition?: number;
};

const TEXT_NODE_IN_ROW_LIMIT = 3;
const VARIANTS_LIMIT = 100;

const EMPTY_SUGGESTION: MultiSuggestion = {
  type: "multi",
  items: [],
  popupGroup: "multi",
  priority: 1,
};

export function createKnownSuggestionMap() {
  const map = new Map<string, SingleSuggestion[]>();

  /**
   * use first keyword as primary match
   * the rest are only used if they're long enough
   * this is a workaround to ignore one-lettered suggestions for things like greek letters
   *
   * FIXME: keyword order should not affect suggestions https://linear.app/corca-research/issue/D-2686/multinode-refactoring
   */
  function filterKeywords(keywords: string[]) {
    return keywords
      .map((kw) => kw.normalize("NFKC"))
      .filter((kw, i) => {
        return /^[a-z]+$/.test(kw) && (i === 0 || kw.length > 1);
      });
  }

  /**
   * most constants are too specific - skip one-lettered constants except for `i` and `e`
   */
  function skipConstant(keyword: string) {
    return (
      !keyword || (hasOneCodepoint(keyword) && !ALLOWED_SINGLE_LETTER_SYMBOLS.includes(keyword))
    );
  }

  /**
   * ignore one-lettered functions except for differential
   */
  function skipFunction(keyword: string) {
    return (
      !keyword || (hasOneCodepoint(keyword) && !ALLOWED_SINGLE_LETTER_SYMBOLS.includes(keyword))
    );
  }

  // for now only the first keyword is used to avoid the flood of irrelevant suggestions.

  for (const constant of constants.filter((c) => c.context?.includes("multi"))) {
    for (const keyword of filterKeywords(Object.keys(constant.item.keywords.en))) {
      if (!skipConstant(keyword)) map.set(keyword, [...(map.get(keyword) ?? []), constant]);
    }
  }

  for (const variable of variables.filter((v) => v.context?.includes("multi"))) {
    for (const keyword of filterKeywords(Object.keys(variable.item.keywords.en))) {
      // skip variable if constant with a same keyword already exists
      if (keyword && !map.has(keyword)) map.set(keyword, [variable]);
    }
  }

  for (const func of [...functions, ...serviceFunctions]) {
    for (const keyword of filterKeywords(Object.keys(func.item.keywords.en))) {
      if (!skipFunction(keyword)) map.set(keyword, [...(map.get(keyword) ?? []), func]);
    }
  }

  return map;
}

export function createSingleLetterSuggestionMap(knownSuggestions: Map<string, SingleSuggestion[]>) {
  const map = new Map<string, SingleSuggestion[]>();
  for (let i = "a".charCodeAt(0); i <= "z".charCodeAt(0); i++) {
    const symbol = String.fromCharCode(i);
    if (knownSuggestions.has(symbol)) {
      map.set(symbol, knownSuggestions.get(symbol)!);
      continue;
    }

    map.set(symbol, [
      {
        type: "single",
        item: createSuggestionItem("variable", symbol, true),
        priority: 1,
      } as SingleSuggestion<"variable">,
    ]);
  }

  return map;
}

function getTextVariants(
  variantText: string,
  { knownSymbolKeywords, knownSuggestionMap, nodeLookup, existingNodes }: TextVariantProps,
  isLast: boolean,
) {
  const cache: Map<string, MultiSuggestion[]> = new Map();

  function getRecursiveVariants(text: string, findIncompleteMatch = false): MultiSuggestion[] {
    if (text === "") return [EMPTY_SUGGESTION];
    if (cache.has(text)) return cache.get(text)!;

    const variants: MultiSuggestion[] = [];

    const exactSymbolKeywords = knownSymbolKeywords.filter((kw) => text.startsWith(kw));
    let symbolKeywords = exactSymbolKeywords;
    if (findIncompleteMatch && text.length > 1) {
      symbolKeywords = symbolKeywords.concat(
        knownSymbolKeywords.filter((key) => key.startsWith(text)),
      );
      symbolKeywords = Array.from(new Set(symbolKeywords));
    }
    symbolKeywords = symbolKeywords.filter(
      (kw) => !nodeLookup?.has(kw, { types: ["variable", "constant"] }),
    );

    const suggestedVariables: string[] = [];

    if (nodeLookup) {
      for (const symbol of nodeLookup.keys) {
        if (!text.startsWith(symbol)) continue;

        if (!symbol.length) {
          console.error("Node with empty string as symbol found", nodeLookup.keys);
          continue;
        }
        const restText = text.slice(symbol.length);
        const restVariants = getRecursiveVariants(restText);

        for (const node of existingNodes.get(symbol) ?? []) {
          const suggestion = node
            ? createSuggestionFromNode(node)
            : ({
                type: "single",
                item: createSuggestionItem("variable", symbol),
                priority: 1,
              } as SingleSuggestion<"variable">);

          for (const restVariant of restVariants) {
            if (restVariant.items.length < 3) {
              const priority = mediumPriority([suggestion.priority, restVariant.priority]);
              variants.push({
                type: "multi",
                items: [suggestion.item, ...restVariant.items],
                priority: increasePriority(priority, 1, 1.1),
              });
            }
          }
        }
      }
    }

    if (symbolKeywords.length > 0) {
      for (const kw of symbolKeywords) {
        let variableSuggestionAdded = false;
        const restText = text.slice(kw.length);
        const restVariants = getRecursiveVariants(restText);

        const kwSuggestions = knownSuggestionMap.get(kw) ?? [];
        for (const knownSuggestion of kwSuggestions) {
          for (const restVariant of restVariants) {
            const items = [knownSuggestion.item, ...restVariant.items];
            const priority = mediumPriority([knownSuggestion.priority, restVariant.priority]);

            if (knownSuggestion.item.type === "variable") {
              suggestedVariables.push(kw);
            }

            if (isAcceptableCombination(items)) {
              variants.push({
                type: "multi",
                items,
                priority: increasePriority(priority, kw.length, 1.1),
              });

              if (
                hasOneCodepoint(kw) &&
                !variableSuggestionAdded &&
                knownSuggestion.item.type === "function" &&
                !nodeLookup?.has(kw) &&
                !kwSuggestions.find((s) => s.item.type !== "function")
              ) {
                variableSuggestionAdded = true;
                variants.push({
                  type: "multi",
                  items: [createSuggestionItem("variable", kw, true), ...restVariant.items],
                  priority,
                });
              }
            }
          }
        }
      }
    }

    cache.set(text, variants);
    return variants;
  }

  return getRecursiveVariants(variantText, isLast);
}

function isDifferentialOrDelta(item: SuggestionItem) {
  return !!(
    item.type === "function" &&
    ((item as SuggestionItem<"function">).isDifferential || item.key === "function-delta")
  );
}

/**
 * do not allow too many variables or constants in a row
 * (functions are fine)
 */
function isAcceptableCombination(items: SuggestionItem[]) {
  let num = 0;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    num = item.type === "function" ? 0 : num + 1;
    if (isDifferentialOrDelta(item)) {
      // allow constructions like dx*dy*dz
      for (let j = i; j < items.length; j += 2) {
        if (!isDifferentialOrDelta(items[j])) return false;
        if (!items[j + 1]) return false;
        if (items[j + 1].type !== "variable" || items[j + 1].isNewNode) return false;
      }
    }
    if (num > TEXT_NODE_IN_ROW_LIMIT) return false;
  }
  return true;
}

function sortVariants(variants: SuggestionVariant[]) {
  for (const v of variants) {
    const maxRepetition = maxRepeatingString(v.variantTypes);
    let { priority } = v;
    priority = increasePriority(priority, maxRepetition ?? 1, 1.5);
    const differentialTypes = v.items.filter(isDifferentialOrDelta).map((item) => item.symbol);
    if (differentialTypes.length > 0) {
      priority = increasePriority(priority, maxRepeatingString(differentialTypes), 1.5);
    }
    v.priority = priority;
  }

  return sortBy(variants, [(v) => -v.priority]);
}

export function filterVariants(variants: SuggestionVariant[]) {
  return variants
    .filter((v) => {
      return v.items.length > 1 || v.variantTypes[0] !== "plain";
    })
    .slice(0, VARIANTS_LIMIT);
}

export function getFlattenVariants(
  variants: SuggestionVariant[][],
  depth = 0,
): SuggestionVariant[] {
  const result: SuggestionVariant[] = [];
  const [first, ...rest] = variants;
  const restVariants = rest.length > 0 ? getFlattenVariants(rest, depth + 1) : [];

  if (restVariants.length === 0) {
    return sortVariants(first);
  }

  for (const variant of first) {
    for (const restVariant of restVariants) {
      const items = [...variant.items, ...restVariant.items];
      const priority = Math.sqrt(variant.priority * restVariant.priority);
      const variantTypes = [...variant.variantTypes, ...restVariant.variantTypes];
      result.push({
        variantTypes,
        type: "multi",
        items,
        priority,
        popupGroup: "multi",
      });
    }
  }

  if (depth > 0) return result;

  return sortVariants(result);
}

/**
 * increase priority based on `increase` number, asymptotically moving up to `max` number
 * when `increase` gose to Infinity, the result will be `max`
 */
export function increasePriority(priority: number, increase: number, max: number) {
  return max - (max - priority) * Math.exp(-increase);
}

/**
 * find the string that repeats the most in an array and return the number of repetitions
 */
function maxRepeatingString(arr: string[]) {
  const map = new Map<string, number>();

  for (const str of arr) {
    map.set(str, (map.get(str) ?? 0) + 1);
  }

  return Math.max(...map.values());
}

function createSuggestionItem(
  type: "number" | "constant" | "variable",
  value: string,
  isNewNode = false,
) {
  switch (type) {
    case "number":
      return {
        type: "number",
        intlName: "number",
        symbol: value,
        value,
        isNewNode,
      } as const;
    case "constant":
      return {
        type: "constant",
        intlName: "constant",
        symbol: value,
        value,
        isNewNode,
      } as const;
    case "variable":
      return {
        type: "variable",
        intlName: "variable",
        symbol: value,
        value,
        isNewNode,
      } as const;
  }
}

function createVariant(values: ["number" | "constant", string][], priority = 1): SuggestionVariant {
  return {
    variantTypes: values.map(() => "plain"),
    type: "multi",
    items: values.map(([type, value]) => createSuggestionItem(type, value)),
    priority,
  };
}

export function mediumPriority(priorities: number[]) {
  return priorities.reduce((acc, p) => acc * (p ?? 1), 1) ** (1 / priorities.length);
}

export function createPowerOperatorItem(num: string): SuggestionItem {
  return {
    type: "operator",
    intlName: "operator.power",
    symbol: "^",
    value: num,
    props: {
      key: "operator-power",
      operatorType: "power",
    },
    insertChildren: [
      [
        {
          type: "number",
          props: { value: num },
        },
      ],
    ],
  };
}

// a substituted existing node (e.g. x_1) carries displayName: node.name, which is
// "" for unnamed variables; recover the base symbol's known display name
// (e.g. "Variable x") so index/power rows read the same as the first-time case
function getKnownDisplayName(
  item: SuggestionItem,
  knownSuggestionMap: Map<string, SingleSuggestion[]>,
): string | undefined {
  if (item.displayName) return item.displayName;
  const known = knownSuggestionMap
    .get(item.symbol)
    ?.find((s) => s.item.type === item.type && s.item.symbol === item.symbol);
  return known?.item.displayName ?? item.displayName;
}

export function getSplitStringSuggestions(
  idx: number,
  text: string,
  num: string | undefined,
  isLast: boolean,
  props: TextVariantProps,
) {
  const variants: SuggestionVariant[] = [];

  if (!text) {
    if (num) variants.push(createVariant([["number", num]]));
    return variants;
  }

  if (text === "i" && idx === 0) {
    if (num) {
      variants.push(
        createVariant([
          ["constant", text],
          ["number", num],
        ]),
      );
    }
  }

  const textVariants = getTextVariants(text, props, isLast);

  for (const textVariant of textVariants) {
    const { priority } = textVariant; // mediumPriority(textVariant.map(v => v.priority ?? 1));

    if (num) {
      const lastItem = textVariant.items.at(-1);
      if (lastItem) {
        const { items } = textVariant;

        const hasLabel = !lastItem.node && !!lastItem.labels?.length;

        const hasSameIndexLabel =
          !lastItem.node &&
          lastItem.labels?.length === 1 &&
          lastItem.labels[0].nodes.length === 1 &&
          lastItem.labels[0].nodes[0].type === "number" &&
          lastItem.labels[0].nodes[0].value === num;

        if (lastItem.type === "function") {
          variants.push({
            variantTypes: ["index"],
            type: "multi",
            items: [...items, ...createVariant([["number", num]]).items],
            priority,
          });

          const funcProps = lastItem.props as FunctionSuggestionProps | undefined;
          // sin2 → sin^2() only for functions rendering power on the name
          if (num !== "1" && (funcProps?.powerPlacement ?? "child") === "child") {
            variants.push({
              variantTypes: ["power"],
              type: "multi",
              items: [...items, createPowerOperatorItem(num)],
              priority,
            });
          }
        } else {
          items.pop();
          // x_2
          if (lastItem.type !== "constant") {
            if (!hasLabel) {
              const labeledItem: SuggestionItem = {
                ...lastItem,
                displayName: getKnownDisplayName(lastItem, props.knownSuggestionMap),
                node: undefined,
                isNewNode: true,
                labels: [
                  {
                    position: "right-bottom",
                    nodes: [
                      {
                        type: "number",
                        value: num,
                      },
                    ],
                  },
                ],
              };
              variants.push({
                variantTypes: ["index"],
                type: "multi",
                items: [...items, labeledItem],
                priority,
              });
            }
          } else if (!hasLabel) {
            // workaround
            const labeledItem: SuggestionItem = {
              ...createSuggestionItem("variable", lastItem.symbol),
              isNewNode: true,
              labels: [
                {
                  position: "right-bottom",
                  nodes: [
                    {
                      type: "number",
                      value: num,
                    },
                  ],
                },
              ],
            };
            variants.push({
              variantTypes: ["index"],
              type: "multi",
              items: [...items, labeledItem],
              priority,
            });
          }

          if (!hasSameIndexLabel) {
            // strip the label carried by a substituted existing node so the
            // power reads the base symbol (x^2), not the existing index (x_1^2)
            const powerBase: SuggestionItem = lastItem.node
              ? {
                  ...lastItem,
                  displayName: getKnownDisplayName(lastItem, props.knownSuggestionMap),
                  node: undefined,
                  labels: undefined,
                }
              : lastItem;
            // x^2
            variants.push({
              variantTypes: ["power"],
              type: "multi",
              items: [...items, powerBase, createPowerOperatorItem(num)],
              priority: num === "1" ? priority : increasePriority(priority, 1, 1.1),
            });
          }
        }
      }
    } else {
      variants.push({
        variantTypes: ["plain"],
        ...textVariant,
      });
    }
  }

  return variants;
}
